import React, { useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Spin, message } from "antd";

import { loadGoogleIdentity } from "../utils/googleIdentity";
import styles from "./Login.module.scss";

const DEFAULT_AFTER_LOGIN_URL = "/units?limit=50&page=1";

/*
 * Post-login redirect from `?next=` (set by /oauth/authorize when LibreChat
 * bounces a logged-out user here). Only our own server's URLs are honoured.
 */
const serverNextUrl = (next) => {
  try {
    const nextUrl = new URL(next);
    const serverUrl = new URL(process.env.REACT_APP_SERVER_URL);
    return nextUrl.origin === serverUrl.origin ? nextUrl.href : null;
  } catch {
    return null;
  }
};

const Login = (props) => {
  const { isAuthenticated, loginUser } = props;
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [signedInHere, setSignedInHere] = useState(false);
  const buttonRef = useRef(null);

  const next = serverNextUrl(searchParams.get("next"));
  const followNext = isAuthenticated && signedInHere && next;

  useEffect(() => {
    if (followNext) window.location.assign(followNext);
  }, [followNext]);

  useEffect(() => {
    if (isAuthenticated) return;

    let cancelled = false;
    loadGoogleIdentity()
      .then((googleId) => {
        if (cancelled || !buttonRef.current) return;
        googleId.initialize({
          client_id: process.env.REACT_APP_GOOGLE_LOGIN_CLIENT_ID,
          ux_mode: "popup",
          auto_select: false,
          callback: async (response) => {
            setIsLoading(true);
            const ok = await loginUser({ credential: response.credential });
            if (ok) setSignedInHere(true);
            else setIsLoading(false);
          },
        });
        buttonRef.current.innerHTML = "";
        googleId.renderButton(buttonRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "signin_with",
          logo_alignment: "center",
          width: 320,
        });
      })
      .catch(() => {
        if (!cancelled) message.error("Could not load Google Sign-In.");
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, loginUser]);

  if (isAuthenticated === true) {
    return followNext ? null : <Navigate to={DEFAULT_AFTER_LOGIN_URL} />;
  }

  return (
    <div className={styles.mainLogin}>
      <div className={styles.loginSvgImage}>
        <div className={styles.loginLogo} />
      </div>
      <div className={styles.loginForm}>
        <Spin spinning={isLoading}>
          <div ref={buttonRef} className={styles.googleButton} />
        </Spin>
      </div>
    </div>
  );
};

export default Login;
